import { useEffect, useState, useRef, useCallback } from "react";
import Layout from "@/components/Layout";
import { useStepNav } from "@/contexts/NavigationContext";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import HistoricalReconcile from "@/pages/_import/HistoricalReconcile";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import {
  DownloadSimpleIcon, DatabaseIcon, CloudArrowDownIcon, FilesIcon,
  CheckCircleIcon, ClockClockwiseIcon, ShieldCheckIcon, WarningCircleIcon,
  UploadSimpleIcon, XIcon, FileXlsIcon, InfoIcon, TrashIcon, ScalesIcon,
  ExportIcon,
  LockKeyIcon, WarningIcon, XCircleIcon
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * Backups del sistema — solo admin/owner.
 * Genera un ZIP en memoria con MongoDB + manifest R2 y lo descarga.
 * NO persiste nada en el servidor (sin consumir espacio en R2 ni Mongo).
 */
export default function Backups() {
  const { user } = useAuth();
  const esAdminGeneral = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipePartialOpen, setWipePartialOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups/manifest");
      setInfo(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    const t = toast.loading("Generando backup… puede tardar 30-90 segundos");
    try {
      const res = await api.get("/backups/download", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : `agroport-backup-${Date.now()}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup descargado", { id: t });
      load(); // refresca el "último backup"
    } catch (e) {
      toast.error(apiErr(e) || "No se pudo generar el backup", { id: t });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout
      title="Copias de seguridad"
      subtitle="Genera y descarga un respaldo completo del sistema en el momento"
    >
      <div className="space-y-5">
        {/* CTA principal */}
        <div className="bg-gradient-to-br from-agro-green/20 via-agro-green/10 to-transparent border border-agro-green/30 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-agro-green/20 flex items-center justify-center flex-shrink-0">
                <ShieldCheckIcon size={28} weight="duotone" className="text-agro-green" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-text-primary mb-1">
                  Respaldo completo del sistema
                </h2>
                <p className="text-sm text-text-muted max-w-xl">
                  Genera un archivo <code className="text-agro-green">.zip</code> con TODA la base de datos MongoDB +
                  el inventario completo de archivos en Cloudflare R2. Se descarga directamente a tu computadora —
                  no ocupa espacio en R2 ni en Mongo.
                </p>
              </div>
            </div>
            <button
              onClick={download}
              disabled={downloading || loading}
              data-testid="backup-download-btn"
              className="inline-flex items-center justify-center gap-2 bg-agro-green hover:bg-agro-green-hover disabled:opacity-50 text-black font-semibold rounded-xl px-6 py-3 transition shadow-lg shadow-agro-green/20 flex-shrink-0"
            >
              <DownloadSimpleIcon size={18} weight="bold" />
              {downloading ? "Generando…" : "Descargar backup ahora"}
            </button>
          </div>
        </div>

        {/* Restaurar card */}
        <div className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent border border-blue-500/30 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                <UploadSimpleIcon size={22} weight="duotone" className="text-blue-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-text-primary mb-0.5">Restaurar desde backup</h3>
                <p className="text-xs text-text-muted max-w-xl">
                  <WarningIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Reemplaza TODA la base de datos con el contenido del ZIP que subas. Solo dueño/superadmin. Requiere confirmación explícita.
                </p>
              </div>
            </div>
            <button
              onClick={() => setRestoreOpen(true)}
              data-testid="backup-restore-btn"
              className="inline-flex items-center justify-center gap-2 border border-blue-500/50 hover:bg-blue-500/10 text-blue-300 font-semibold rounded-xl px-5 py-2.5 transition flex-shrink-0"
            >
              <UploadSimpleIcon size={16} weight="bold" />
              Montar backup
            </button>
          </div>
        </div>

        {/* Importar desde Excel: exclusivo del Administrador General */}
        {esAdminGeneral && <div className="bg-gradient-to-br from-agro-yellow/10 via-agro-yellow/5 to-transparent border border-agro-yellow/30 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-agro-yellow/15 flex items-center justify-center flex-shrink-0">
                <FileXlsIcon size={22} weight="duotone" className="text-agro-yellow" />
              </div>
              <div>
                <h3 className="text-base font-bold text-text-primary mb-0.5">Importar desde Excel</h3>
                <p className="text-xs text-text-muted max-w-xl">
                  Carga inicial de datos (clientes, proveedores, productos, etc.) desde plantillas Excel.
                  Selecciona una entidad, descarga su plantilla, llénala y súbela con validación previa.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button
                onClick={() => setImportOpen(true)}
                data-testid="excel-import-btn"
                className="inline-flex items-center justify-center gap-2 border border-agro-yellow/50 hover:bg-agro-yellow/10 text-agro-yellow font-semibold rounded-xl px-5 py-2.5 transition"
              >
                <FileXlsIcon size={16} weight="bold" />
                Abrir importador
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Esto marcará las Facturas Históricas importadas como historial comercial (saldo 0) y revierte el saldo que se sumó a los clientes. No toca los Estados de Cuenta ni las históricas que aún conservan una deuda declarada (esas se separan en 'Revisión de facturas históricas'). ¿Continuar?")) return;
                  try {
                    const { data } = await api.post("/excel-imports/fix-historical-sales");
                    toast.success(`Corregidas ${data.facturas_corregidas} facturas · L ${data.saldo_liberado.toLocaleString()} liberados de CxC · ${data.clientes_afectados} clientes ajustados`);
                  } catch (e) { toast.error(apiErr(e)); }
                }}
                data-testid="fix-historical-btn"
                className="inline-flex items-center justify-center gap-2 border border-border hover:border-agro-yellow/40 text-text-secondary text-xs font-medium rounded-xl px-5 py-2 transition"
                title="Las ventas históricas importadas dejan de generar Cuentas por Cobrar"
              >
                Corregir ventas históricas (quitar de CxC)
              </button>
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.get("/excel-imports/historical-summary");
                    const lines = [
                      `Diagnóstico CxC ↔ Históricas`,
                      ``,
                      `Ventas históricas (solo estadística): ${data.ventas_historicas_solo_estadistica}`,
                      `Históricas AÚN con saldo por conciliar: ${data.historicas_aun_con_saldo_por_conciliar}`,
                      `Saldos de apertura vigentes (deben permanecer): ${data.saldos_apertura_vigentes}`,
                      `Facturas operativas: ${data.facturas_operativas}`,
                      ``,
                      `Saldo históricas por conciliar: L ${data.saldo_historicas_por_conciliar.toLocaleString()}`,
                      `Saldo apertura vigente: L ${data.saldo_apertura_vigente.toLocaleString()}`,
                      ``,
                      `Recomendación: ${data.recomendacion}`,
                    ];
                    window.alert(lines.join("\n"));
                  } catch (e) { toast.error(apiErr(e)); }
                }}
                data-testid="hist-summary-btn"
                className="inline-flex items-center justify-center gap-2 border border-border hover:border-agro-yellow/40 text-text-muted text-xs font-medium rounded-xl px-5 py-2 transition"
                title="Muestra el conteo de facturas históricas y saldos vigentes para decidir qué reconciliar"
              >
                Diagnóstico CxC ↔ Históricas
              </button>
            </div>
          </div>
        </div>}

        {/* Historial comercial vs cuentas por cobrar: revisión de cargas históricas */}
        {esAdminGeneral && <HistoricalReconcile isAdmin />}

        {/* Asiento de apertura */}
        <div className="bg-gradient-to-br from-agro-green/10 via-agro-green/5 to-transparent border border-agro-green/30 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-agro-green/15 flex items-center justify-center flex-shrink-0">
                <ShieldCheckIcon size={22} weight="duotone" className="text-agro-green" />
              </div>
              <div>
                <h3 className="text-base font-bold text-text-primary mb-0.5">Asiento contable de apertura</h3>
                <p className="text-xs text-text-muted max-w-xl">
                  Consolida todos los saldos importados (CxC clientes, CxP proveedores, bancos, inventario) en
                  un único asiento contable balanceado que cierra la apertura de libros.
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpeningOpen(true)}
              data-testid="opening-entry-btn"
              className="inline-flex items-center justify-center gap-2 border border-agro-green/50 hover:bg-agro-green/10 text-agro-green font-semibold rounded-xl px-5 py-2.5 transition flex-shrink-0"
            >
              <ShieldCheckIcon size={16} weight="bold" />
              Generar asiento
            </button>
          </div>
        </div>

        {/* Reconciliación de saldos */}
        <div className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent border border-blue-500/30 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                <ScalesIcon size={22} weight="duotone" className="text-blue-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-text-primary mb-0.5">Reconciliar saldos de clientes</h3>
                <p className="text-xs text-text-muted max-w-xl">
                  Compara <code className="text-blue-300">clients.saldo</code> vs. Σ facturas pendientes.
                  Detecta desfases (por ajustes manuales, imports erróneos, borrados) y los arregla ajustando el saldo del cliente al total real de facturas pendientes.
                </p>
              </div>
            </div>
            <button
              onClick={() => setReconcileOpen(true)}
              data-testid="reconcile-btn"
              className="inline-flex items-center justify-center gap-2 border border-blue-500/50 hover:bg-blue-500/10 text-blue-300 font-semibold rounded-xl px-5 py-2.5 transition flex-shrink-0"
            >
              <ScalesIcon size={16} weight="bold" />
              Verificar saldos
            </button>
          </div>
        </div>

        {/* Exportador Excel */}
        <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/30 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                <ExportIcon size={22} weight="duotone" className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-text-primary mb-0.5">Exportar a Excel</h3>
                <p className="text-xs text-text-muted max-w-xl">
                  Descarga cualquier entidad (clientes, proveedores, productos, empleados, bancos, etc.)
                  al mismo formato del importador. Útil para respaldos rápidos o migración a otro sistema.
                </p>
              </div>
            </div>
            <button
              onClick={() => setExportOpen(true)}
              data-testid="export-excel-btn"
              className="inline-flex items-center justify-center gap-2 border border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-300 font-semibold rounded-xl px-5 py-2.5 transition flex-shrink-0"
            >
              <ExportIcon size={16} weight="bold" />
              Abrir exportador
            </button>
          </div>
        </div>

        {/* Zona peligrosa: Wipe de datos demo */}
        <div className="bg-gradient-to-br from-red-500/10 via-red-500/5 to-transparent border border-red-500/40 rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <TrashIcon size={22} weight="duotone" className="text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-red-300 mb-0.5">Zona peligrosa · Borrar datos</h3>
                <p className="text-xs text-text-muted max-w-xl">
                  <strong className="text-agro-yellow">Borrar por partes:</strong> elimina solo una carga puntual
                  (ej. solo proveedores subidos ayer) sin tocar el resto. Ideal para rehacer una importación específica.
                  <br />
                  <strong className="text-red-300">Borrar todo:</strong> elimina TODA la data operativa
                  y activa la marca permanente para que el sistema no vuelva a sembrar data demo al reiniciar.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <button
                onClick={() => setWipePartialOpen(true)}
                data-testid="wipe-partial-btn"
                className="inline-flex items-center justify-center gap-2 border border-agro-yellow/50 hover:bg-agro-yellow/15 text-agro-yellow font-semibold rounded-xl px-5 py-2.5 transition"
              >
                <TrashIcon size={16} weight="bold" />
                Borrar por partes
              </button>
              <button
                onClick={() => setWipeOpen(true)}
                data-testid="wipe-demo-btn"
                className="inline-flex items-center justify-center gap-2 border border-red-500/50 hover:bg-red-500/15 text-red-300 font-semibold rounded-xl px-5 py-2.5 transition"
              >
                <TrashIcon size={16} weight="bold" />
                Borrar todo
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-text-muted text-sm">Cargando información…</div>
        )}

        {!loading && info && (
          <>
            {/* Cards info */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card
                icon={DatabaseIcon}
                title="Base de datos MongoDB"
                value={info.mongo.total_documents.toLocaleString("es-HN")}
                sub={`${info.mongo.collections} colecciones`}
                color="text-agro-green"
                testid="backup-mongo-info"
              />
              <Card
                icon={CloudArrowDownIcon}
                title="Almacenamiento R2"
                value={info.r2.object_count.toLocaleString("es-HN")}
                sub={info.r2.configured
                  ? `${formatBytes(info.r2.total_size_bytes)} en archivos`
                  : "R2 no configurado"}
                color={info.r2.configured ? "text-agro-yellow" : "text-text-muted"}
                testid="backup-r2-info"
              />
            </div>

            {/* Último backup */}
            <div className="bg-card border border-border/60 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <ClockClockwiseIcon size={18} className="text-text-muted" />
                <h3 className="text-sm font-semibold text-text-primary">Último backup descargado</h3>
              </div>
              {info.last_backup ? (
                <div className="text-sm text-text-secondary">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircleIcon size={14} weight="fill" className="text-agro-green" />
                    <span>{fmtDate(info.last_backup.timestamp)}</span>
                  </div>
                  <div className="text-xs text-text-muted ml-6">
                    Generado por: <span className="text-text-primary">{info.last_backup.user_email || "—"}</span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-text-muted flex items-center gap-2">
                  <WarningCircleIcon size={14} className="text-orange-400" />
                  Aún no se ha generado ningún backup. Recomendado: generar uno semanal.
                </div>
              )}
            </div>

            {/* Detalle por colección */}
            <details className="bg-card border border-border/60 rounded-2xl p-5" data-testid="backup-collections-detail">
              <summary className="cursor-pointer text-sm font-semibold text-text-primary flex items-center gap-2">
                <FilesIcon size={18} className="text-text-muted" />
                Detalle por colección
                <span className="text-xs font-normal text-text-muted">(click para expandir)</span>
              </summary>
              <div className="mt-3 max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-text-muted uppercase border-b border-border/40">
                    <tr><th className="text-left py-2">Colección</th><th className="text-right">Documentos</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(info.mongo.by_collection)
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, n]) => (
                        <tr key={name} className="border-b border-border/20 hover:bg-white/[0.02]">
                          <td className="py-1.5 text-text-secondary font-mono text-xs">{name}</td>
                          <td className="text-right text-text-primary tabular-nums">{n.toLocaleString("es-HN")}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </details>

            <div className="text-xs text-text-muted bg-app/30 border border-border/40 rounded-lg p-4">
              ℹ️ <strong>Recomendación:</strong> descarga un backup al menos una vez por semana y guárdalo en
              un disco externo o en la nube (Google Drive / OneDrive). Esto te protege ante cualquier
              accidente con MongoDB Atlas o Cloudflare R2.
              <br />
              <LockKeyIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> El backup incluye datos sensibles — guárdalo en un lugar seguro y no lo compartas.
            </div>
          </>
        )}
      </div>

      {restoreOpen && (
        <RestoreModal
          onClose={() => setRestoreOpen(false)}
          onDone={() => { setRestoreOpen(false); load(); }}
        />
      )}
      {importOpen && (
        <ExcelImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); }}
        />
      )}
      {openingOpen && (
        <OpeningEntryModal
          onClose={() => setOpeningOpen(false)}
          onDone={() => { setOpeningOpen(false); load(); }}
        />
      )}
      {wipeOpen && (
        <WipeDemoModal
          onClose={() => setWipeOpen(false)}
          onDone={() => { setWipeOpen(false); load(); }}
        />
      )}
      {wipePartialOpen && (
        <WipePartialModal
          onClose={() => setWipePartialOpen(false)}
          onDone={() => { setWipePartialOpen(false); load(); }}
        />
      )}
      {reconcileOpen && (
        <ReconcileModal
          onClose={() => setReconcileOpen(false)}
        />
      )}
      {exportOpen && (
        <ExportExcelModal
          onClose={() => setExportOpen(false)}
        />
      )}
    </Layout>
  );
}

function RestoreModal({ onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = confirm.trim().toUpperCase() === "RESTAURAR AGROPORT" && file;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    const t = toast.loading("Restaurando base de datos… NO cierres la ventana");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", confirm);
      const { data } = await api.post("/backups/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
      });
      toast.success(
        `Restauración OK: ${data.restored_count} colecciones · ${data.total_documents.toLocaleString("es-HN")} documentos`,
        { id: t, duration: 7000 },
      );
      onDone();
    } catch (e) {
      toast.error(apiErr(e) || "No se pudo restaurar", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const vm = useModalForm({ value: file ? file.name : undefined, onClose: onClose, onSave: submit, mensaje: "¿Está seguro de que desea restaurar esta copia de seguridad? Reemplaza los datos actuales." });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="restore-modal"
     {...vm.overlayProps}>
      <div className="bg-card border border-red-500/30 rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
              <WarningCircleIcon size={22} weight="fill" className="text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-text-primary">Restaurar backup</h3>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="restore-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-5">
          <div className="text-sm text-red-300 font-semibold mb-1 flex items-center gap-1"><WarningIcon size={14} aria-hidden="true" /> ACCIÓN DESTRUCTIVA</div>
          <p className="text-xs text-red-200/80 leading-relaxed">
            Esta acción <strong>elimina y reemplaza</strong> TODA la información de MongoDB con la del ZIP que subas.
            No hay forma de revertirlo. Asegúrate de tener un backup reciente ANTES de continuar.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              1. Archivo .zip del backup
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="restore-file-input"
              className="w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-white/5 file:text-text-primary file:cursor-pointer file:hover:bg-white/10"
            />
            {file && <div className="text-[11px] text-agro-green mt-1"><CheckCircleIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> {file.name} ({formatBytes(file.size)})</div>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              2. Escribe exactamente <code className="text-red-300">RESTAURAR AGROPORT</code> para confirmar
            </label>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="restore-confirm-input"
              placeholder="RESTAURAR AGROPORT"
              className="w-full bg-app border border-border focus:border-red-500/60 rounded-md px-3 py-2 text-sm outline-none font-mono"
              autoComplete="off"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={vm.cerrar}
            disabled={busy}
            className="px-4 py-2 text-sm border border-border rounded-md hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            onClick={vm.guardar}
            disabled={(!valid || busy) || vm.saving}
            data-testid="restore-submit-btn"
            className="px-5 py-2 text-sm bg-red-500 hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed text-white font-semibold rounded-md transition"
          >
            {busy ? "Restaurando…" : "Confirmar y restaurar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, value, sub, color, testid }) {
  return (
    <div className="bg-card border border-border/60 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} weight="duotone" className={color} />
        <div className="text-xs font-semibold tracking-wide uppercase text-text-muted">{title}</div>
      </div>
      <div className={`text-3xl font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-xs text-text-muted mt-1">{sub}</div>
    </div>
  );
}

function formatBytes(n) {
  if (!n || n < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-HN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

/** Resumen previo / final de las cargas históricas: facturas, líneas, coincidencias, duplicados y errores. */
function ImportSummary({ entity, s, decision }) {
  const hist = entity === "invoices_history";
  const items = hist ? [
    ["Facturas", s.facturas], ["Líneas", s.lineas], ["Nuevas", s.nuevas], ["Duplicadas (omitidas)", s.duplicadas_omitidas],
    ["Conflictos", (s.conflictos || []).length], ["Actualizadas", s.actualizadas],
    ["Relacionadas con estado de cuenta", s.coincidencias_estado_cuenta], ["Incidencias", (s.incidencias || []).length],
    ["Filas con error", s.errores],
  ] : [
    ["Obligaciones nuevas", s.saldos_creados], ["Saldo total (L)", (s.saldo_total || 0).toLocaleString()],
    ["Relacionadas con historial", s.relacionadas_con_historial], ["Incidencias", (s.incidencias || []).length],
    ["Filas con error", s.errores],
  ];
  return (
    <div className="mb-3 space-y-2" data-testid="import-summary">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {items.map(([l, v]) => (
          <div key={l} className="bg-card border border-border/50 rounded p-2">
            <div className="text-[10px] text-text-muted">{l}</div>
            <div className="text-sm font-semibold">{v ?? 0}</div>
          </div>
        ))}
      </div>
      {hist && <div className="text-[11px] text-agro-green">Historial comercial: no genera saldos por cobrar, cuentas por pagar, contabilidad, caja/bancos ni inventario.</div>}
      {(s.conflictos || []).length > 0 && (
        <details className="text-[11px] border border-agro-yellow/30 bg-agro-yellow/5 rounded p-2" open>
          <summary className="cursor-pointer text-agro-yellow">
            Conflictos con facturas ya importadas ({s.conflictos.length}) — {decision === "actualizar" ? "se actualizará su historial" : "se omitirán"}
          </summary>
          <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
            {s.conflictos.map((c, i) => (
              <li key={i}><span className="font-mono">{c.numero}</span> · {c.cliente}: {c.antes.lineas} líneas / L {c.antes.total.toLocaleString()} ({c.antes.fecha}) → archivo {c.archivo.lineas} líneas / L {c.archivo.total.toLocaleString()} ({c.archivo.fecha})</li>
            ))}
          </ul>
        </details>
      )}
      {(s.incidencias || []).length > 0 && (
        <details className="text-[11px] border border-orange-500/30 bg-orange-500/5 rounded p-2">
          <summary className="cursor-pointer text-orange-300">Incidencias para revisión — no se vinculan automáticamente ({s.incidencias.length})</summary>
          <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
            {s.incidencias.map((x, i) => <li key={i}><span className="font-mono">{x.numero}</span> · {x.cliente}: {x.motivo}</li>)}
          </ul>
        </details>
      )}
      {(s.avisos || []).length > 0 && (
        <details className="text-[11px] text-text-muted"><summary className="cursor-pointer">Avisos ({s.avisos.length})</summary>
          <ul className="mt-1 space-y-0.5">{s.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul></details>
      )}
    </div>
  );
}

// Actualización de precios: resumen de la vista previa / resultado (precio anterior → nuevo)
function PreciosSummary({ s }) {
  const fmt = (n) => Number(n || 0).toLocaleString("es-HN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const items = [
    ["Productos a actualizar", s.productos_actualizar], ["Precios de venta nuevos o cambiados", s.precios_cargar],
    ["Precios de costo cargados", s.costos_cargar],
    ["Filas sin cambios", s.sin_cambios], ["Quedarán sin precio completo", s.siguen_sin_precio], ["Filas con error", s.errores],
  ];
  return (
    <div className="mb-3 space-y-2" data-testid="precios-summary">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {items.map(([l, v]) => (
          <div key={l} className="bg-card border border-border/50 rounded p-2">
            <div className="text-[10px] text-text-muted">{l}</div>
            <div className="text-sm font-semibold text-text-primary">{v ?? 0}</div>
          </div>
        ))}
      </div>
      {(s.bajo_costo || []).length > 0 && (
        <details className="text-[11px]" data-testid="precios-bajo-costo">
          <summary className="cursor-pointer text-agro-yellow">
            Aviso: {s.bajo_costo.length} precio(s) por debajo del costo ponderado — se importan igual; revisa si es correcto
          </summary>
          <div className="mt-1 max-h-32 overflow-y-auto font-mono text-text-secondary">
            {s.bajo_costo.map((b, i) => <div key={i}>{b.codigo} · {b.nivel}: L {fmt(b.precio)} &lt; costo L {fmt(b.costo)}</div>)}
          </div>
        </details>
      )}
      {(s.cambios || []).length > 0 && (
        <details className="text-[11px]" open data-testid="precios-cambios">
          <summary className="cursor-pointer text-text-secondary">
            Cambios de precio ({s.cambios_total}{s.cambios_total > s.cambios.length ? `, se muestran ${s.cambios.length}` : ""})
          </summary>
          <div className="mt-1 max-h-48 overflow-y-auto border border-border/40 rounded">
            <table className="w-full">
              <thead className="bg-app text-text-muted uppercase text-[10px] sticky top-0">
                <tr><th className="p-1.5 text-left">Código</th><th className="p-1.5 text-left">Producto</th>
                  <th className="p-1.5 text-center">Nivel</th><th className="p-1.5 text-right">Anterior</th><th className="p-1.5 text-right">Nuevo</th></tr>
              </thead>
              <tbody>
                {s.cambios.map((c, i) => (
                  <tr key={i} className="border-t border-border/20">
                    <td className="p-1.5 font-mono whitespace-nowrap">{c.codigo}</td>
                    <td className="p-1.5 truncate max-w-[16rem]">{c.nombre}</td>
                    <td className="p-1.5 text-center">{c.nivel}</td>
                    <td className="p-1.5 text-right font-mono text-text-muted">{c.antes ? fmt(c.antes) : "sin precio"}</td>
                    <td className="p-1.5 text-right font-mono text-agro-green">{fmt(c.despues)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

export function ExcelImportModal({ onClose, onDone, initialEntity = "", initialFiltro = "sin_precio" }) {
  const [entities, setEntities] = useState([]);
  const [entityKey, setEntityKey] = useState("");
  // Actualización de precios: la plantilla trae el catálogo (por defecto solo productos sin precio completo)
  const [filtroPrecios, setFiltroPrecios] = useState(initialFiltro);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("select");   // select | preview | done
  const [result, setResult] = useState(null);
  // Facturas ya importadas con diferencias: decisión explícita (omitir por defecto / actualizar historial)
  const [conflictos, setConflictos] = useState("omitir");

  useEffect(() => {
    api.get("/excel-imports/entities")
      .then((r) => {
        setEntities(r.data.entities || []);
        const ini = (r.data.entities || []).find((e) => e.key === initialEntity);
        if (ini) setEntityKey(ini.key);
        else if (r.data.entities?.length) setEntityKey(r.data.entities[0].key);
      })
      .catch((e) => toast.error(apiErr(e) || "No se pudo cargar entidades"));
  }, [initialEntity]);

  const current = entities.find((e) => e.key === entityKey);

  // Proceso de varios pasos: las flechas del ERP recorren Selección ⇄ Vista previa. "Siguiente" solo
  // vuelve a una vista previa ya validada (no valida ni confirma la importación por su cuenta).
  const FASES = ["select", "preview", "done"];
  const idx = FASES.indexOf(phase);
  const pasoPrev = useCallback(() => setPhase("select"), []);
  const pasoNext = useCallback(() => setPhase("preview"), []);
  useStepNav({
    label: "Importación · paso", index: idx, total: 3,
    canPrev: phase === "preview" && !busy,
    canNext: phase === "select" && Boolean(preview) && !busy,
    prev: pasoPrev, next: pasoNext,
  });

  const downloadTemplate = async () => {
    if (!entityKey) return;
    const t = toast.loading("Generando plantilla…");
    try {
      const precios = entityKey === "precios";
      const res = await api.get(`/excel-imports/${entityKey}/template`, {
        responseType: "blob", params: precios ? { filtro: filtroPrecios } : undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = precios
        ? `plantilla-${{ sin_precio: "productos-sin-precio", sin_costo: "productos-sin-costo" }[filtroPrecios] || "precios-catalogo"}-agroport.xlsx`
        : `plantilla-${entityKey}-agroport.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada", { id: t });
    } catch (e) { toast.error(apiErr(e) || "Error", { id: t }); }
  };

  const runPreview = async () => {
    if (!file || !entityKey) return;
    setBusy(true);
    const t = toast.loading("Validando archivo…");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("conflictos", conflictos);
      const { data } = await api.post(`/excel-imports/${entityKey}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setPhase("preview");
      toast.success(`${data.total} filas · ${data.valid} válidas · ${data.invalid} con errores`, { id: t });
    } catch (e) { toast.error(apiErr(e) || "Error al validar", { id: t }); }
    finally { setBusy(false); }
  };

  const runCommit = async () => {
    if (!file || !entityKey) return;
    const sm = preview?.summary || {};
    const extra = entityKey === "invoices_history"
      ? `\n\nFacturas nuevas: ${sm.nuevas ?? 0} · líneas: ${sm.lineas ?? 0} · duplicadas omitidas: ${sm.duplicadas_omitidas ?? 0}` +
        ` · conflictos: ${(sm.conflictos || []).length} (${conflictos === "actualizar" ? "se actualizarán" : "se omitirán"})` +
        ` · relacionadas con estados de cuenta: ${sm.coincidencias_estado_cuenta ?? 0}.\nNo genera saldos por cobrar ni movimientos contables o de inventario.`
      : entityKey === "precios"
        ? `\n\nSe actualizarán ${sm.precios_cargar ?? 0} precio(s) en ${sm.productos_actualizar ?? 0} producto(s).` +
          (sm.bajo_costo?.length ? ` ${sm.bajo_costo.length} quedan por debajo del costo.` : "") +
          "\nSolo cambian los precios de venta P1–P4 y el costo de catálogo: no se tocan existencias, kardex ni documentos emitidos."
        : "";
    if (!window.confirm(`¿Confirmas la importación de ${preview?.valid || 0} registros? Los que tengan errores se saltarán.${extra}`)) return;
    setBusy(true);
    const t = toast.loading("Importando registros…");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("conflictos", conflictos);
      const { data } = await api.post(`/excel-imports/${entityKey}/commit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPhase("done");
      toast.success(entityKey === "precios"
        ? `Precios actualizados en ${data.inserted} producto(s) · ${data.skipped} fila(s) con error`
        : `Importación OK: ${data.inserted} insertados · ${data.skipped} saltados`, { id: t, duration: 6000 });
    } catch (e) { toast.error(apiErr(e) || "Error al importar", { id: t }); }
    finally { setBusy(false); }
  };

  const vm = useModalForm({ value: phase === "done" ? undefined : (file ? file.name : undefined), onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="excel-import-modal" {...vm.overlayProps}>
      <div className="bg-card border border-agro-yellow/30 rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-border/50 sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-agro-yellow/15 flex items-center justify-center">
              <FileXlsIcon size={22} weight="fill" className="text-agro-yellow" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-text-primary">Importar desde Excel</h3>
              <p className="text-xs text-text-muted">Selecciona la entidad, descarga la plantilla y sube tu archivo lleno.</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="excel-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Aviso multi-sucursal */}
          <div className="bg-agro-yellow/5 border border-agro-yellow/30 rounded-lg p-3 flex items-start gap-2">
            <InfoIcon size={16} className="text-agro-yellow flex-shrink-0 mt-0.5" />
            <div className="text-xs text-text-secondary">
              <strong className="text-agro-yellow">Multi-sucursal:</strong> los clientes, proveedores y productos se comparten
              entre todas las sucursales de la misma empresa (RTN y código son únicos por empresa). Cada registro guarda
              la <strong className="text-text-primary">sucursal activa</strong> donde fue creado para trazabilidad. Los movimientos
              (facturas, pagos, inventario, estados de cuenta) sí se agrupan y filtran por sucursal.
            </div>
          </div>

          {/* Selector de entidad */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
              1. Tipo de entidad a importar
            </label>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
              {entities.map((e) => (
                <button
                  key={e.key}
                  onClick={() => { setEntityKey(e.key); setFile(null); setPreview(null); setPhase("select"); setConflictos("omitir"); }}
                  data-testid={`entity-pick-${e.key}`}
                  className={`text-left p-3 rounded-lg border transition ${
                    entityKey === e.key
                      ? "bg-agro-yellow/10 border-agro-yellow text-text-primary"
                      : "bg-app border-border/60 hover:border-agro-yellow/50 text-text-secondary"
                  }`}
                >
                  <div className="text-sm font-semibold">{e.title}</div>
                  <div className="text-[11px] text-text-muted mt-0.5 line-clamp-2">{e.description}</div>
                </button>
              ))}
            </div>
          </div>

          {current && (
            <>
              <div className={`rounded-lg p-3 text-xs border ${["invoices_history", "statements"].includes(current.key)
                ? "bg-agro-green/5 border-agro-green/30 text-text-primary" : "bg-app/40 border-border/60 text-text-secondary"}`}
                data-testid="entity-description">
                <strong>{current.title}:</strong> {current.description}
              </div>
              {current.key === "invoices_history" && (
                <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="conflict-decision">
                  <span className="text-text-muted">Si una factura ya importada llega con diferencias:</span>
                  {[["omitir", "Omitir y mostrar el conflicto"], ["actualizar", "Actualizar su historial comercial"]].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="conflictos" checked={conflictos === v} data-testid={`conflict-${v}`}
                        onChange={() => { setConflictos(v); setPreview(null); setPhase("select"); }} /> {l}
                    </label>
                  ))}
                </div>
              )}
              {/* Columnas esperadas */}
              <details className="bg-app/40 border border-border/60 rounded-lg p-3">
                <summary className="cursor-pointer text-xs font-semibold text-text-secondary flex items-center gap-2">
                  <InfoIcon size={14} className="text-agro-yellow" />
                  Columnas esperadas ({current.columns.length}) · click para ver
                </summary>
                <div className="mt-3 grid gap-2 max-h-60 overflow-y-auto">
                  {current.columns.map((c) => (
                    <div key={c.key} className="text-xs flex items-start gap-2 border-b border-border/20 pb-2 last:border-0">
                      <span className={`font-mono px-2 py-0.5 rounded border ${c.required ? "bg-red-500/10 border-red-500/30 text-red-300" : "bg-white/[0.03] border-border text-text-secondary"}`}>
                        {c.key}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-text-primary">{c.label} {c.required && <span className="text-red-400">*</span>}</div>
                        {c.hint && <div className="text-text-muted text-[10px] mt-0.5">{c.hint}</div>}
                      </div>
                      {c.example && <div className="text-[10px] font-mono text-text-muted">ej: {c.example}</div>}
                    </div>
                  ))}
                </div>
              </details>

              {/* Descargar plantilla */}
              <div className="flex items-center gap-3">
                <button
                  onClick={downloadTemplate}
                  data-testid="download-template-btn"
                  className="inline-flex items-center gap-2 bg-agro-yellow/15 hover:bg-agro-yellow/25 border border-agro-yellow/40 text-agro-yellow font-medium rounded-lg px-4 py-2 text-sm"
                >
                  <DownloadSimpleIcon size={14} weight="bold" />
                  {entityKey === "precios" ? "Descargar plantilla con mis productos" : "Descargar plantilla Excel"}
                </button>
                {entityKey === "precios" ? (
                  <label className="flex items-center gap-1.5 text-xs text-text-secondary">
                    Productos:
                    <select value={filtroPrecios} onChange={(e) => setFiltroPrecios(e.target.value)} data-testid="precios-filtro"
                      className="bg-app border border-border rounded-md px-2 py-1 text-xs text-text-primary">
                      <option value="sin_precio">Solo los que no tienen precio de venta completo</option>
                      <option value="sin_costo">Solo los que no tienen precio de costo</option>
                      <option value="todos">Todo el catálogo</option>
                    </select>
                  </label>
                ) : <span className="text-xs text-text-muted">Contiene encabezados + fila de ejemplo</span>}
              </div>

              {/* Subida */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
                  2. Sube tu archivo Excel lleno
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setPhase("select"); }}
                  data-testid="excel-file-input"
                  className="w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-white/5 file:text-text-primary file:cursor-pointer file:hover:bg-white/10"
                />
                {file && (
                  <div className="text-[11px] text-agro-green mt-1">
                    OK - {file.name} ({formatBytes(file.size)})
                  </div>
                )}
              </div>

              {/* Preview */}
              {phase === "preview" && preview && (
                <div className="bg-app/40 border border-border/60 rounded-lg p-4">
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div><span className="text-text-muted">Total:</span> <span className="font-semibold">{preview.total}</span></div>
                    <div><span className="text-text-muted">Válidas:</span> <span className="font-semibold text-agro-green">{preview.valid}</span></div>
                    <div><span className="text-text-muted">Con errores:</span> <span className={`font-semibold ${preview.invalid > 0 ? "text-red-400" : "text-text-secondary"}`}>{preview.invalid}</span></div>
                    {preview.truncated && <div className="text-xs text-agro-yellow">(mostrando primeras 10,000)</div>}
                  </div>
                  {preview.summary && ["invoices_history", "statements"].includes(entityKey) && (
                    <ImportSummary entity={entityKey} s={preview.summary} decision={conflictos} />
                  )}
                  {preview.summary && entityKey === "precios" && <PreciosSummary s={preview.summary} />}
                  <div className="max-h-72 overflow-y-auto border border-border/40 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-app text-text-muted uppercase text-[10px] sticky top-0">
                        <tr>
                          <th className="p-2 text-left">#</th>
                          <th className="p-2 text-left">Estado</th>
                          <th className="p-2 text-left">Vista previa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r) => (
                          <tr key={r.row_number} className={`border-t border-border/20 ${r.ok ? "" : "bg-red-500/5"}`}>
                            <td className="p-2 font-mono">{r.row_number}</td>
                            <td className="p-2">
                              {r.ok
                                ? <span className="text-agro-green">OK</span>
                                : <div className="text-red-300 text-[10px]">{r.errors.join(" · ")}</div>}
                            </td>
                            <td className="p-2 text-text-secondary truncate max-w-md">
                              {Object.values(r.data).filter(Boolean).slice(0, 4).join(" · ")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Result */}
              {phase === "done" && result && (
                <div className="bg-agro-green/10 border border-agro-green/40 rounded-lg p-4 text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon size={18} weight="fill" className="text-agro-green" />
                    <span className="font-semibold text-text-primary">Importación finalizada</span>
                  </div>
                  {result.summary && ["invoices_history", "statements"].includes(entityKey) && (
                    <ImportSummary entity={entityKey} s={result.summary} decision={conflictos} />
                  )}
                  <div className="text-text-secondary">
                    <strong className="text-agro-green">{result.inserted}</strong>
                    {entityKey === "precios" ? " productos con precios actualizados ·" : " registros insertados ·"}
                    {" "}{result.skipped} saltados por errores ·
                    total {result.total} filas
                  </div>
                  {result.errors?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-red-300">Ver errores ({result.errors.length})</summary>
                      <div className="mt-2 max-h-40 overflow-y-auto text-[11px] font-mono text-red-200 space-y-1">
                        {result.errors.map((e, i) => (
                          <div key={i}>Fila {e.row_number}: {e.errors.join(", ")}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Botones */}
              <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
                <button onClick={vm.cerrar} disabled={busy}
                  className="px-4 py-2 text-sm border border-border rounded-md hover:bg-white/5">
                  {phase === "done" ? "Cerrar" : "Cancelar"}
                </button>
                {phase === "select" && (
                  <button
                    onClick={runPreview}
                    disabled={!file || busy}
                    data-testid="excel-preview-btn"
                    className="px-5 py-2 text-sm bg-agro-yellow/20 hover:bg-agro-yellow/30 border border-agro-yellow/40 text-agro-yellow disabled:opacity-30 disabled:cursor-not-allowed font-semibold rounded-md transition"
                  >
                    {busy ? "Validando…" : "Validar archivo"}
                  </button>
                )}
                {phase === "preview" && (
                  <button
                    onClick={runCommit}
                    disabled={!preview || preview.valid === 0 || busy}
                    data-testid="excel-commit-btn"
                    className="px-5 py-2 text-sm bg-agro-green hover:bg-agro-green-hover disabled:opacity-30 disabled:cursor-not-allowed text-black font-semibold rounded-md transition"
                  >
                    {busy ? "Importando…" : `Confirmar e importar (${preview.valid})`}
                  </button>
                )}
                {phase === "done" && (
                  <button
                    onClick={() => { onDone(); }}
                    data-testid="excel-done-btn"
                    className="px-5 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md transition"
                  >
                    Listo
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function OpeningEntryModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/opening-entry/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(apiErr(e) || "No se pudo cargar el preview"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const commit = async () => {
    if (!preview?.balanced) return;
    if (!window.confirm("¿Confirmas la creación del asiento de apertura? Esta acción no se puede deshacer directamente (deberás eliminarlo desde Contabilidad).")) return;
    setBusy(true);
    const t = toast.loading("Registrando asiento contable…");
    try {
      const { data } = await api.post("/opening-entry/commit");
      toast.success(`Asiento #${data.numero} creado (L ${Number(data.total_debe).toLocaleString("es-HN", { minimumFractionDigits: 2 })})`, { id: t, duration: 6000 });
      onDone();
    } catch (e) { toast.error(apiErr(e) || "Error al registrar", { id: t }); }
    finally { setBusy(false); }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="opening-entry-modal" {...vm.overlayProps}>
      <div className="bg-card border border-agro-green/30 rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-border/50 sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-agro-green/15 flex items-center justify-center">
              <ShieldCheckIcon size={22} weight="fill" className="text-agro-green" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-text-primary">Asiento contable de apertura</h3>
              <p className="text-xs text-text-muted">Consolida los saldos importados en un solo asiento contable balanceado.</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="opening-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="text-center py-8 text-text-muted">Calculando saldos…</div>
          ) : !preview ? (
            <div className="text-center py-8 text-red-400">No se pudo cargar el preview</div>
          ) : (
            <>
              {preview.already_exists && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                  <WarningCircleIcon size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-text-secondary">
                    <strong className="text-red-300">Ya existe asiento #{preview.already_exists.numero}</strong> del {" "}
                    {preview.already_exists.fecha?.slice(0, 10)} con total L {Number(preview.already_exists.total_debe).toLocaleString("es-HN")}.
                    Elimínalo desde Contabilidad para regenerar.
                  </div>
                </div>
              )}

              {/* Resumen ACTIVOS */}
              <div className="bg-app/40 border border-agro-green/30 rounded-lg overflow-hidden">
                <div className="bg-agro-green/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-agro-green">
                  DEBE — Activos
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <RowActivo label="1101 · Caja General" desc={`${preview.totals.activos.caja_detail.length} caja(s)`} monto={preview.totals.activos.caja} />
                    <RowActivo label="1102 · Bancos" desc={`${preview.totals.activos.bancos_detail.length} cuenta(s)`} monto={preview.totals.activos.bancos} />
                    <RowActivo label="1103 · Cuentas por Cobrar" desc={`${preview.totals.activos.cxc_detail.length} cliente(s)`} monto={preview.totals.activos.cxc} />
                    <RowActivo label="1104 · Inventarios" desc={`${preview.totals.activos.inventario_count} SKU(s) valorados`} monto={preview.totals.activos.inventario} />
                    <tr className="border-t-2 border-agro-green/40 bg-agro-green/5">
                      <td className="px-4 py-2 font-bold text-agro-green">TOTAL ACTIVOS</td>
                      <td className="px-4 py-2 text-right font-bold text-agro-green text-base">L {preview.totals.activos.total.toLocaleString("es-HN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Resumen PASIVOS + PLUG */}
              <div className="bg-app/40 border border-red-500/20 rounded-lg overflow-hidden">
                <div className="bg-red-500/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-red-300">
                  HABER — Pasivos + Patrimonio
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <RowActivo label="2101 · CxP Proveedores" desc={`${preview.totals.pasivos.cxp_detail.length} proveedor(es)`} monto={preview.totals.pasivos.cxp} debe={false} />
                    <RowActivo label={`${preview.totals.plug.cuenta} · ${preview.totals.plug.nombre}`} desc="Contrapartida de saldos de apertura (plug)" monto={preview.totals.plug.monto} debe={preview.totals.plug.lado === "debe"} />
                    <tr className="border-t-2 border-red-500/30 bg-red-500/5">
                      <td className="px-4 py-2 font-bold text-red-300">TOTAL PASIVO + PATRIMONIO</td>
                      <td className="px-4 py-2 text-right font-bold text-red-300 text-base">L {(preview.totals.pasivos.total + preview.totals.plug.monto).toLocaleString("es-HN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Balance check */}
              <div className={`rounded-lg p-3 flex items-center justify-between ${preview.balanced ? "bg-agro-green/10 border border-agro-green/30" : "bg-red-500/10 border border-red-500/30"}`}>
                <div className="flex items-center gap-2">
                  {preview.balanced ? (
                    <CheckCircleIcon size={20} weight="fill" className="text-agro-green" />
                  ) : (
                    <WarningCircleIcon size={20} weight="fill" className="text-red-400" />
                  )}
                  <span className={`font-semibold ${preview.balanced ? "text-agro-green" : "text-red-300"}`}>
                    {preview.balanced ? "Asiento balanceado" : "Asiento NO balanceado"}
                  </span>
                </div>
                <div className="text-xs font-mono text-text-muted">
                  DEBE L {preview.total_debe.toLocaleString("es-HN", { minimumFractionDigits: 2 })} · HABER L {preview.total_haber.toLocaleString("es-HN", { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
                <button onClick={vm.cerrar} disabled={busy}
                  className="px-4 py-2 text-sm border border-border rounded-md hover:bg-white/5">
                  Cancelar
                </button>
                <button
                  onClick={commit}
                  disabled={!preview.balanced || busy || !!preview.already_exists || preview.lineas.length === 0}
                  data-testid="opening-commit-btn"
                  className="px-5 py-2 text-sm bg-agro-green hover:bg-agro-green-hover disabled:opacity-30 disabled:cursor-not-allowed text-black font-semibold rounded-md transition"
                >
                  {busy ? "Registrando…" : "Confirmar y registrar"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RowActivo({ label, desc, monto, debe = true }) {
  return (
    <tr className="border-t border-border/20">
      <td className="px-4 py-2">
        <div className="text-text-primary font-medium">{label}</div>
        <div className="text-[11px] text-text-muted">{desc}</div>
      </td>
      <td className={`px-4 py-2 text-right font-mono ${monto > 0 ? (debe ? "text-agro-green" : "text-red-300") : "text-text-muted"}`}>
        L {Number(monto).toLocaleString("es-HN", { minimumFractionDigits: 2 })}
      </td>
    </tr>
  );
}


function WipeDemoModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/wipe-preview")
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(apiErr(e) || "No se pudo cargar preview"))
      .finally(() => setLoading(false));
  }, []);

  const commit = async () => {
    if (confirmText !== "BORRAR TODO") return;
    setBusy(true);
    const t = toast.loading("Borrando data demo…");
    try {
      const { data } = await api.post("/admin/wipe-demo-data", { confirmation: "BORRAR TODO" });
      setResult(data);
      toast.success(`Borrados ${data.total_deleted} documentos de ${data.collections_wiped} colecciones`, { id: t, duration: 8000 });
    } catch (e) { toast.error(apiErr(e) || "Error", { id: t }); }
    finally { setBusy(false); }
  };

  const alreadyWiped = preview?.marker?.value === true;

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="wipe-demo-modal" {...vm.overlayProps}>
      <div className="bg-card border border-red-500/40 rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-red-500/30 sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
              <TrashIcon size={22} weight="fill" className="text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-300">Borrar todos los datos de prueba</h3>
              <p className="text-xs text-text-muted">Acción no reversible. Conserva solo lo mínimo del sistema.</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="wipe-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="text-center py-8 text-text-muted">Calculando…</div>
          ) : result ? (
            <div className="bg-agro-green/10 border border-agro-green/40 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircleIcon size={20} weight="fill" className="text-agro-green" />
                <span className="font-semibold text-text-primary">Wipe completado</span>
              </div>
              <div className="text-sm text-text-secondary mb-3">
                <strong className="text-agro-green">{result.total_deleted}</strong> documentos borrados en {" "}
                <strong>{result.collections_wiped}</strong> colecciones. Marca de seed-disabled persistida.
                Ahora puedes cargar tus datos reales vía el importador Excel.
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-text-muted">Ver detalle por colección</summary>
                <div className="mt-2 max-h-52 overflow-y-auto text-[11px] font-mono space-y-0.5">
                  {result.wiped.map((w) => (
                    <div key={w.collection} className="text-text-secondary">
                      <XCircleIcon size={12} className="inline align-[-2px] text-red-300" aria-label="Borrado" /> {w.collection}: {w.before} → 0 ({w.deleted} borrados)
                    </div>
                  ))}
                </div>
              </details>
              <div className="mt-4 flex justify-end">
                <button onClick={onDone} data-testid="wipe-done-btn"
                  className="px-5 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md">
                  Listo
                </button>
              </div>
            </div>
          ) : (
            <>
              {alreadyWiped && (
                <div className="bg-agro-yellow/10 border border-agro-yellow/40 rounded-lg p-3 flex items-start gap-2">
                  <InfoIcon size={16} className="text-agro-yellow flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-text-secondary">
                    <strong className="text-agro-yellow">Ya fue ejecutado antes</strong> por {" "}
                    <code className="text-agro-yellow">{preview.marker.disabled_by}</code> el {" "}
                    {new Date(preview.marker.disabled_at).toLocaleString("es-HN")}. Puedes volver a ejecutar
                    si algo se resembró manualmente.
                  </div>
                </div>
              )}

              {/* Preservado */}
              <div className="bg-agro-green/5 border border-agro-green/20 rounded-lg overflow-hidden">
                <div className="bg-agro-green/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-agro-green flex items-center gap-2">
                  <ShieldCheckIcon size={14} weight="bold" />
                  Se conservará ({preview?.preserved?.length || 0})
                </div>
                <div className="p-3 flex flex-wrap gap-1.5">
                  {preview?.preserved?.map((p) => (
                    <span key={p.collection} className="text-[10px] font-mono bg-agro-green/10 border border-agro-green/20 text-text-secondary rounded px-2 py-0.5">
                      {p.collection} ({p.count})
                    </span>
                  ))}
                </div>
              </div>

              {/* A borrar */}
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg overflow-hidden">
                <div className="bg-red-500/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-red-300 flex items-center gap-2">
                  <TrashIcon size={14} weight="bold" />
                  Se borrará ({preview?.to_wipe?.length || 0} colecciones · {preview?.total_to_delete || 0} documentos)
                </div>
                <div className="p-3 max-h-52 overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5">
                    {preview?.to_wipe?.map((p) => (
                      <span key={p.collection} className="text-[10px] font-mono bg-red-500/10 border border-red-500/20 text-red-200 rounded px-2 py-0.5">
                        {p.collection} ({p.count})
                      </span>
                    ))}
                    {!preview?.to_wipe?.length && (
                      <span className="text-xs text-text-muted italic">Nada por borrar — la base ya está limpia.</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Confirmation */}
              <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4">
                <label className="block text-xs font-semibold uppercase tracking-wider text-red-300 mb-2">
                  Para confirmar, escribe exactamente: <code className="bg-black/30 px-2 py-0.5 rounded">BORRAR TODO</code>
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="BORRAR TODO"
                  autoComplete="off"
                  data-testid="wipe-confirm-input"
                  className="w-full bg-app border border-border/60 rounded-md px-3 py-2 text-sm font-mono text-text-primary focus:outline-none focus:border-red-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
                <button onClick={vm.cerrar} disabled={busy}
                  className="px-4 py-2 text-sm border border-border rounded-md hover:bg-white/5">
                  Cancelar
                </button>
                <button
                  onClick={commit}
                  disabled={confirmText !== "BORRAR TODO" || busy || !preview?.to_wipe?.length}
                  data-testid="wipe-commit-btn"
                  className="px-5 py-2 text-sm bg-red-500 hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed text-white font-bold rounded-md transition"
                >
                  {busy ? "Borrando…" : `Sí, borrar ${preview?.total_to_delete || 0} documentos`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function WipePartialModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/wipe-partial/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(apiErr(e) || "No se pudo cargar preview"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const totalSelected = (preview?.entities || [])
    .filter((e) => selected.has(e.key))
    .reduce((s, e) => s + (e.total || 0), 0);

  const commit = async () => {
    if (confirmText !== "BORRAR") return;
    if (selected.size === 0) return;
    setBusy(true);
    const t = toast.loading("Borrando…");
    try {
      const { data } = await api.post("/admin/wipe-partial", {
        entities: Array.from(selected),
        confirmation: "BORRAR",
      });
      setResult(data);
      toast.success(`Borrados ${data.total_deleted} documentos`, { id: t, duration: 6000 });
    } catch (e) {
      toast.error(apiErr(e) || "Error", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-16 md:pt-20 overflow-y-auto" data-testid="wipe-partial-modal" {...vm.overlayProps}>
      <div className="bg-card border border-agro-yellow/40 rounded-2xl max-w-3xl w-full max-h-[calc(100vh-6rem)] overflow-y-auto shadow-2xl my-auto">
        <div className="flex items-start justify-between p-6 border-b border-agro-yellow/30 sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-agro-yellow/15 flex items-center justify-center">
              <TrashIcon size={22} weight="fill" className="text-agro-yellow" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-agro-yellow">Borrar por partes</h3>
              <p className="text-xs text-text-muted">Selecciona qué carga(s) rehacer. Solo se borran las entidades marcadas.</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="wipe-partial-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="text-center py-8 text-text-muted">Calculando…</div>
          ) : result ? (
            <div className="bg-agro-green/10 border border-agro-green/40 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircleIcon size={20} weight="fill" className="text-agro-green" />
                <span className="font-semibold text-text-primary">Wipe parcial completado</span>
              </div>
              <div className="text-sm text-text-secondary mb-3">
                <strong className="text-agro-green">{result.total_deleted}</strong> documentos borrados en {result.entities.length} entidad(es).
                {result.client_saldo_synced && <> Saldos de clientes re-sincronizados.</>}
                {result.supplier_saldo_synced && <> Saldos de proveedores re-sincronizados.</>}
                {result.product_stock_synced && <> Stock de productos recalculado.</>}
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-text-muted">Ver detalle</summary>
                <div className="mt-2 max-h-52 overflow-y-auto text-[11px] font-mono space-y-0.5">
                  {result.wiped.map((w, i) => (
                    <div key={i} className="text-text-secondary">
                      <XCircleIcon size={12} className="inline align-[-2px] text-agro-yellow" aria-label="Borrado" /> {w.entity} → {w.collection}: {w.before} → 0 ({w.deleted} borrados)
                    </div>
                  ))}
                </div>
              </details>
              <div className="mt-4 flex justify-end">
                <button onClick={onDone} data-testid="wipe-partial-done-btn"
                  className="px-5 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md">
                  Listo
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-agro-yellow/10 border border-agro-yellow/40 rounded-lg p-3 flex items-start gap-2">
                <InfoIcon size={16} className="text-agro-yellow flex-shrink-0 mt-0.5" />
                <div className="text-xs text-text-secondary">
                  Marca las entidades que quieres borrar. Ejemplo: si subiste mal los proveedores ayer,
                  marca solo <strong className="text-agro-yellow">Proveedores</strong> y vuelve a importar.
                  Los saldos de clientes/proveedores se re-sincronizan automáticamente al borrar apertura.
                </div>
              </div>

              <div className="border border-border/60 rounded-lg overflow-hidden">
                {(preview?.entities || []).map((e) => (
                  <label
                    key={e.key}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-border/40 last:border-0 cursor-pointer transition ${selected.has(e.key) ? "bg-agro-yellow/10" : "hover:bg-white/[0.03]"}`}
                    data-testid={`wipe-partial-row-${e.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(e.key)}
                      onChange={() => toggle(e.key)}
                      disabled={e.total === 0}
                      className="w-4 h-4 accent-agro-yellow disabled:opacity-30"
                      data-testid={`wipe-partial-check-${e.key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary">{e.title}</div>
                      <div className="text-[11px] text-text-muted font-mono truncate">
                        {e.targets.map((t) => `${t.collection}(${t.count})`).join(" · ")}
                      </div>
                    </div>
                    <div className={`text-sm font-heading font-semibold ${e.total > 0 ? "text-agro-yellow" : "text-text-muted"}`}>
                      {e.total}
                    </div>
                  </label>
                ))}
              </div>

              <div className="bg-agro-yellow/10 border border-agro-yellow/40 rounded-lg p-4">
                <label className="block text-xs font-semibold uppercase tracking-wider text-agro-yellow mb-2">
                  Escribe <code className="bg-black/30 px-2 py-0.5 rounded">BORRAR</code> para confirmar
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="BORRAR"
                  autoComplete="off"
                  data-testid="wipe-partial-confirm-input"
                  className="w-full bg-app border border-border/60 rounded-md px-3 py-2 text-sm font-mono text-text-primary focus:outline-none focus:border-agro-yellow"
                />
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-border/40">
                <div className="text-xs text-text-muted">
                  {selected.size === 0
                    ? "Sin selección"
                    : <>Seleccionadas <strong className="text-agro-yellow">{selected.size}</strong> entidad(es) · <strong className="text-agro-yellow">{totalSelected}</strong> documentos</>}
                </div>
                <div className="flex gap-2">
                  <button onClick={vm.cerrar} disabled={busy}
                    className="px-4 py-2 text-sm border border-border rounded-md hover:bg-white/5">
                    Cancelar
                  </button>
                  <button
                    onClick={commit}
                    disabled={confirmText !== "BORRAR" || busy || selected.size === 0 || totalSelected === 0}
                    data-testid="wipe-partial-commit-btn"
                    className="px-5 py-2 text-sm bg-agro-yellow hover:brightness-110 disabled:opacity-30 disabled:cursor-not-allowed text-black font-bold rounded-md transition"
                  >
                    {busy ? "Borrando…" : `Borrar ${totalSelected} documentos`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}



function ReconcileModal({ onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/reconcile-balances/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(apiErr(e) || "No se pudo cargar"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fix = async () => {
    if (!window.confirm("¿Ajustar todos los saldos al total calculado de facturas pendientes?")) return;
    setBusy(true);
    const t = toast.loading("Ajustando saldos…");
    try {
      const { data } = await api.post("/admin/reconcile-balances/fix");
      toast.success(`${data.fixed} cliente(s) ajustado(s)`, { id: t, duration: 5000 });
      load();
    } catch (e) { toast.error(apiErr(e) || "Error", { id: t }); }
    finally { setBusy(false); }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-blue-500/40 rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-border/50 flex items-center justify-between sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <ScalesIcon size={22} weight="fill" className="text-blue-400" />
            <div>
              <h3 className="text-lg font-bold">Reconciliar saldos</h3>
              <p className="text-xs text-text-muted">Comparativa <code>clients.saldo</code> vs. Σ facturas pendientes</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar}><XIcon size={18} /></button>
        </div>
        <div className="p-5">
          {loading ? <div className="text-center py-8 text-text-muted">Calculando…</div> : preview ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-app/60 border border-border/60 rounded-lg p-3 text-center">
                  <div className="text-xs text-text-muted">Total clientes</div>
                  <div className="text-2xl font-bold">{preview.total_clientes}</div>
                </div>
                <div className="bg-agro-green/10 border border-agro-green/30 rounded-lg p-3 text-center">
                  <div className="text-xs text-text-muted">Correctos</div>
                  <div className="text-2xl font-bold text-agro-green">{preview.ok}</div>
                </div>
                <div className={`border rounded-lg p-3 text-center ${preview.con_desfase > 0 ? "bg-red-500/10 border-red-500/30" : "bg-app/60 border-border/60"}`}>
                  <div className="text-xs text-text-muted">Con desfase</div>
                  <div className={`text-2xl font-bold ${preview.con_desfase > 0 ? "text-red-300" : "text-text-primary"}`}>{preview.con_desfase}</div>
                </div>
              </div>

              {preview.desfases.length > 0 ? (
                <div className="max-h-72 overflow-y-auto border border-border/40 rounded-lg mb-4">
                  <table className="w-full text-xs">
                    <thead className="bg-app text-text-muted uppercase text-[10px] sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Cliente</th>
                        <th className="p-2 text-right">Registrado</th>
                        <th className="p-2 text-right">Calculado</th>
                        <th className="p-2 text-right">Desfase</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.desfases.map((d) => (
                        <tr key={d.cliente_id} className="border-t border-border/20">
                          <td className="p-2">
                            <div className="text-text-primary">{d.codigo}</div>
                            <div className="text-[10px] text-text-muted">{d.nombre}</div>
                          </td>
                          <td className="p-2 text-right font-mono">L {d.saldo_registrado.toLocaleString("es-HN", { minimumFractionDigits: 2 })}</td>
                          <td className="p-2 text-right font-mono text-blue-300">L {d.saldo_calculado.toLocaleString("es-HN", { minimumFractionDigits: 2 })}</td>
                          <td className={`p-2 text-right font-mono font-semibold ${d.desfase > 0 ? "text-red-300" : "text-agro-yellow"}`}>
                            {d.desfase > 0 ? "+" : ""}L {d.desfase.toLocaleString("es-HN", { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-agro-green/5 border border-agro-green/30 rounded-lg p-4 text-center">
                  <CheckCircleIcon size={24} weight="fill" className="mx-auto text-agro-green mb-2" />
                  <div className="text-sm font-semibold text-agro-green">Todos los saldos están correctos</div>
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-border/40 pt-3">
                <button onClick={vm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cerrar</button>
                {preview.desfases.length > 0 && (
                  <button onClick={fix} disabled={busy} data-testid="reconcile-fix-btn"
                    className="px-5 py-2 text-sm bg-blue-500 hover:bg-blue-600 disabled:opacity-30 text-white font-semibold rounded-md">
                    {busy ? "Ajustando…" : `Ajustar ${preview.desfases.length} saldo(s)`}
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ExportExcelModal({ onClose }) {
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/excel-imports/entities")
      .then((r) => setEntities((r.data.entities || []).filter((e) => !["invoices_history", "statements", "payables", "precios"].includes(e.key))))
      .catch((e) => toast.error(apiErr(e) || "Error"));
  }, []);

  const exportEnt = async (ent) => {
    setBusy(ent.key);
    const t = toast.loading(`Exportando ${ent.title}…`);
    try {
      const res = await api.get(`/excel-exports/${ent.key}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `export-${ent.key}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${ent.title} exportado`, { id: t });
    } catch (e) { toast.error(apiErr(e) || "Error", { id: t }); }
    finally { setBusy(""); }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-emerald-500/40 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ExportIcon size={22} weight="fill" className="text-emerald-400" />
            <div>
              <h3 className="text-lg font-bold">Exportar a Excel</h3>
              <p className="text-xs text-text-muted">Descarga cada entidad al formato del importador</p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar}><XIcon size={18} /></button>
        </div>
        <div className="p-5 grid gap-2">
          {entities.map((e) => (
            <button
              key={e.key}
              onClick={() => exportEnt(e)}
              disabled={busy === e.key}
              data-testid={`export-${e.key}`}
              className="text-left p-3 rounded-lg border border-border/60 hover:border-emerald-500/60 hover:bg-emerald-500/5 flex items-center justify-between disabled:opacity-50"
            >
              <div>
                <div className="text-sm font-semibold text-text-primary">{e.title}</div>
                <div className="text-[11px] text-text-muted line-clamp-1">{e.description}</div>
              </div>
              <span className="text-emerald-400 text-sm">
                {busy === e.key ? "…" : "Descargar"}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

